import { PasswordResetRequiredError, UserNotConfirmedError } from '../../types';
import type { ServiceSignInResult } from '../../types';
import type { IdentityProvider } from '../identity-provider';
import type { HttpClient } from '../client-app/index';
import { tokenExchange } from './token-exchange';
import type { TokenExchangeRegistrationFields } from './token-exchange';
import { IdpErrorName, translateIdpError } from './errors';
import { IdpSignInStep } from '../../constants';
import type { AuthLogger } from '../../shared/auth-logger';

export async function cognitoSignIn(
  idp: IdentityProvider,
  http: HttpClient,
  email: string,
  password: string,
  bootstrap: TokenExchangeRegistrationFields | undefined,
  t: (key: string, opts?: { defaultValue: string }) => string,
  logger?: AuthLogger,
  retryCount = 0,
): Promise<ServiceSignInResult> {
  try {
    logger?.log(IdpSignInStep.SignInAttempt, { email: email.replace(/(.{2}).*(@.*)/, '$1***$2') });
    const output = await idp.signIn(email, password);

    if (output.step === IdpSignInStep.RequiresEmailVerification) {
      throw new UserNotConfirmedError(email);
    }
    if (output.step === IdpSignInStep.RequiresNewPassword) {
      throw new PasswordResetRequiredError(email);
    }

    const idToken = await idp.getIdToken();
    const result = await tokenExchange(http, idToken, bootstrap, logger);
    if (result.needsPhone) {
      return { needsPhone: true };
    }
    if (!result.user) throw new Error('invalid token exchange response');
    return { user: result.user, needsPhone: false };
  } catch (error) {
    if (error instanceof UserNotConfirmedError) throw error;
    if (error instanceof PasswordResetRequiredError) throw error;

    const name = (error as { name?: string } | null)?.name;
    if (name === IdpErrorName.UserNotConfirmed) {
      throw new UserNotConfirmedError(email);
    }
    if (name === IdpErrorName.PasswordResetRequired) {
      throw new PasswordResetRequiredError(email);
    }
    if (name === IdpErrorName.UserAlreadyAuthenticated) {
      if (retryCount >= 1) {
        translateIdpError(error, t);
      }
      try { await idp.signOutLocally(); } catch (err) { logger?.log(IdpSignInStep.ClearStaleSessionError, { error: err instanceof Error ? err.message : String(err) }); }
      return cognitoSignIn(idp, http, email, password, bootstrap, t, logger, retryCount + 1);
    }

    translateIdpError(error, t);
  }
}
